/**
 * A test call to the agent from the dashboard, as it is saved right now.
 *
 * THE CALL ITSELF IS `lib/useCall.ts`, shared with the public demo page. What is Try it's own
 * is how it starts - `POST /v1/agent/try`, which starts the hosted agent on Pipecat Cloud with a
 * token for this one call and files it as a test call - the per-hour count it reports, and the
 * link to the call's record once the pod has reported it.
 */

import { useState } from "react";
import { api, post } from "../../api";
import { useCall, type CallSession } from "../../lib/useCall";

export type { Line, Phase } from "../../lib/useCall";

/** How long to keep asking whether the call's record has landed, for the link to it. */
const FIND_CALL_TRIES = 12;
const FIND_CALL_EVERY_MS = 2500;

export function useTestCall(agentId: string, onStarted?: (remaining: number | undefined) => void) {
  const [callRow, setCallRow] = useState<number | null>(null);

  /** The record lands after the pod hangs up, so the link appears a few seconds later. */
  async function findCall(id: string) {
    for (let attempt = 0; attempt < FIND_CALL_TRIES; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, FIND_CALL_EVERY_MS));
      try {
        const found = await api<{ call: { id: number } | null }>(
          `/v1/agent/try/${encodeURIComponent(id)}?agent=${encodeURIComponent(agentId)}`,
        );
        if (found.call) {
          setCallRow(found.call.id);
          return;
        }
      } catch {
        return;
      }
    }
  }

  const core = useCall(
    async () => {
      setCallRow(null);
      const session = await post<CallSession & { remaining?: number }>("/v1/agent/try", { agent: agentId });
      onStarted?.(session.remaining);
      return session;
    },
    {
      onEnded: (id) => {
        if (id) void findCall(id);
      },
      failure: "The test call could not be started. Try again in a moment.",
    },
  );

  return { ...core, callRow };
}

/**
 * The call page, with test calls shown - a Try it call is one, and the calls list hides them
 * unless asked.
 */
export function withTestCalls(path: string): string {
  const [base, query = ""] = path.split("?");
  const params = new URLSearchParams(query);
  params.set("test", "true");
  return `${base}?${params.toString()}`;
}
